
import ConfirmRow, { fmtPhone } from '../parts/ConfirmRow.vue'

export default {
    components: { ConfirmRow },
    props: {
        survey: { type: Object, required: true },
        appointment: { type: Object, default: null }, // {datetime, timezone, method, agent_name, manage_token}
        // True while the page's cancelAppointment() call to
        // cancelScheduledAppointment is in flight — disables both inline
        // confirm buttons so a slow request can't be double-submitted.
        canceling: { type: Boolean, default: false },
    },
    data () {
        return { cancelConfirming: false, county: null }
    },
    computed: {
        firstName () { return this.survey.preferred_name || this.survey.first_name },
        // yearMinus2 is the tax year the income need-list item refers to,
        // same getter/relationship IncomeStep.vue uses (store/index.js:1633).
        yearMinus2 () { return this.$store.getters.yearMinus2 },
        rows () {
            const s = this.survey
            const name = [s.first_name, s.middle_name, s.last_name].filter(Boolean).join(' ')
            return [
                {
                    key: 'name',
                    label: 'Legal name',
                    type: 'name',
                    value: name,
                    editValue: { first: s.first_name || '', middle: s.middle_name || '', last: s.last_name || '' }
                },
                {
                    key: 'dob',
                    label: 'Date of birth',
                    type: 'date',
                    value: this.fmtDate(s.dob),
                    editValue: s.dob,
                    min: '1900-01-01',
                    max: this.$moment().format('YYYY-MM-DD'),
                    sub: 'Used to calculate your Medicare eligibility window'
                },
                {
                    key: 'gender',
                    label: 'Birth gender',
                    type: 'options',
                    value: this.optionText(this.$store.getters.genders, s.gender),
                    editValue: s.gender,
                    options: this.$store.getters.genders
                },
                { key: 'cell_phone', label: 'Phone', type: 'phone', value: fmtPhone(s.cell_phone) || '—', editValue: s.cell_phone },
                { key: 'zip_code', label: 'ZIP code', type: 'zip', value: s.zip_code || '—', editValue: s.zip_code, sub: this.county },
                {
                    key: 'medicare_parts',
                    label: 'Current Medicare',
                    type: 'options',
                    value: this.optionText(this.$store.getters.medicareParts, s.medicare_parts),
                    editValue: s.medicare_parts,
                    options: this.$store.getters.medicareParts
                },
                {
                    key: 'collecting_ssi',
                    label: 'Collecting Social Security',
                    type: 'options',
                    value: s.collecting_ssi ? 'Yes' : 'No',
                    editValue: s.collecting_ssi,
                    options: [{ text: 'Yes', value: true }, { text: 'No', value: false }]
                },
                {
                    key: 'hsa',
                    label: 'Contributing to an HSA',
                    type: 'options',
                    value: s.hsa ? 'Yes' : 'No',
                    editValue: s.hsa,
                    options: [{ text: 'Yes', value: true }, { text: 'No', value: false }]
                },
                {
                    key: 'coverage',
                    label: 'Coverage needed by',
                    type: 'coverage',
                    value: this.coverageDisplay(s),
                    // {coverage_urgency, effective_date} — ConfirmRow's composite
                    // editor (designer feedback #598 item 6, mirrors the v2
                    // scheduler's MedicareSituation.vue coverage-urgency block).
                    editValue: { coverage_urgency: s.coverage_urgency || null, effective_date: s.effective_date || null },
                    // CheckCardGroup renders opt.label, but the store's
                    // coverageUrgencies getter returns {value, text} (same
                    // text->label remap IncomeStep.vue does for its own
                    // CheckCardGroup options, IncomeStep.vue:120-128).
                    options: this.$store.getters.coverageUrgencies.map(o => ({ value: o.value, label: o.text })),
                },
            ]
        },
        apptWhen () {
            if (!this.appointment || !this.appointment.datetime) { return '' }
            const m = this.appointment.timezone
                ? this.$moment.tz(this.appointment.datetime, this.appointment.timezone)
                : this.$moment(this.appointment.datetime)
            return m.format('dddd, MMMM D [at] h:mm A z')
        },
        methodIcon () {
            if (!this.appointment) { return 'calendar-alt' }
            return { video: 'video', email: 'envelope' }[this.appointment.method] || 'phone'
        },
        methodLabel () {
            if (!this.appointment) { return '' }
            return { phone: 'Phone call', video: 'Video call', email: 'Email' }[this.appointment.method] || 'Consultation'
        },
        apptDetail () {
            if (!this.appointment || !this.appointment.agent_name) { return '' }
            if (this.appointment.method === 'video') { return `${this.appointment.agent_name} will meet you online` }
            if (this.appointment.method === 'phone' && this.survey.cell_phone) {
                return `${this.appointment.agent_name} will call you at ${fmtPhone(this.survey.cell_phone)}`
            }
            return this.appointment.agent_name
        },
    },
    watch: {
        'survey.zip_code': { immediate: true, handler: 'loadCounty' },
    },
    methods: {
        fmtDate (iso) { return iso ? this.$moment(iso).format('MM/DD/YYYY') : '—' },
        optionText (options, value) { return (options.find(o => o.value === value) || {}).text || '—' },
        // 'EXACT' shows the chosen month/year ("April 2026" — coverage always
        // starts the 1st, so the day is redundant); any other urgency answer
        // shows its own label ("As soon as possible", etc). Falls back to the
        // old full-date format for legacy rows saved before coverage_urgency
        // existed (urgency null, effective_date already on file).
        coverageDisplay (s) {
            if (s.coverage_urgency === 'EXACT') {
                return s.effective_date ? this.$moment(s.effective_date).format('MMMM YYYY') : '—'
            }
            if (s.coverage_urgency) {
                return this.optionText(this.$store.getters.coverageUrgencies, s.coverage_urgency)
            }
            return s.effective_date ? this.$moment(s.effective_date).format('MMMM D, YYYY') : '—'
        },
        // Response shape verified against Location.vue / zip-county.js's applyCounty:
        // { counties: [{ fips, county, state, ... }] } — `county` is the
        // already-suffixed display name (e.g. "Kent County"), `name` is a
        // fallback some call sites use instead.
        async loadCounty (zip) {
            if (!/^\d{5}$/.test(zip || '')) { this.county = null; return }
            const resp = await this.$api.getCountiesForZip(zip).catch(() => null)
            const c = (resp && resp.counties && resp.counties[0]) || null
            const name = c && (c.county || c.name)
            this.county = name ? [name, c.state].filter(Boolean).join(', ') : null
        },
        onRowSave (key, raw) {
            if (key === 'name') {
                this.$emit('patch', { first_name: raw.first, middle_name: raw.middle, last_name: raw.last })
                return
            }
            if (key === 'coverage') {
                this.$emit('patch', { coverage_urgency: raw.coverage_urgency, effective_date: raw.effective_date })
                return
            }
            this.$emit('patch', { [key]: raw })
        },
    },
}
